import Device from '@/utils/device';
import { logger } from './logger';

export const VISUAL_TRACKING_PREVIEW_PARAM = 'visualTrackingPreview';
export const VISUAL_TRACKING_PREVIEW_PLATFORM_PARAM = 'visualTrackingPlatform';
export const VISUAL_TRACKING_PREVIEW_SESSION_KEY =
  'ehr-web:visual-tracking-preview';
export const VISUAL_TRACKING_PREVIEW_PLATFORM_SESSION_KEY =
  'ehr-web:visual-tracking-preview-platform';
export const VISUAL_TRACKING_WORKBENCH_ROUTE = 'tracking';

export type VisualTrackingPreviewPlatform = 'pc' | 'mobile';

export interface ResolveVisualTrackingPreviewModeInput {
  search: string;
  isFramed: boolean;
  storage?: Storage | null;
}

export interface ResolveVisualTrackingPreviewPlatformInput {
  search: string;
  isPreviewMode: boolean;
  isFramed: boolean;
  storage?: Storage | null;
}

function canUseStorage(storage?: Storage | null): storage is Storage {
  return Boolean(
    storage &&
      typeof storage.getItem === 'function' &&
      typeof storage.setItem === 'function'
  );
}

function isPreviewPlatform(
  value: string | null
): value is VisualTrackingPreviewPlatform {
  return value === 'pc' || value === 'mobile';
}

export function resolveVisualTrackingPreviewMode(
  input: ResolveVisualTrackingPreviewModeInput
): boolean {
  const params = new URLSearchParams(input.search || '');
  const hasPreviewParam = params.get(VISUAL_TRACKING_PREVIEW_PARAM) === '1';

  if (hasPreviewParam) {
    if (canUseStorage(input.storage)) {
      try {
        input.storage.setItem(VISUAL_TRACKING_PREVIEW_SESSION_KEY, '1');
      } catch (error) {
        logger.warn('save visual tracking preview flag failed', error);
      }
    }

    return true;
  }

  if (!input.isFramed || !canUseStorage(input.storage)) {
    return false;
  }

  try {
    return input.storage.getItem(VISUAL_TRACKING_PREVIEW_SESSION_KEY) === '1';
  } catch (error) {
    logger.warn('read visual tracking preview flag failed', error);
    return false;
  }
}

export function resolveVisualTrackingPreviewPlatform(
  input: ResolveVisualTrackingPreviewPlatformInput
): VisualTrackingPreviewPlatform | undefined {
  if (!input.isPreviewMode || !input.isFramed) {
    return undefined;
  }

  const params = new URLSearchParams(input.search || '');
  const platform = params.get(VISUAL_TRACKING_PREVIEW_PLATFORM_PARAM);

  if (isPreviewPlatform(platform)) {
    if (canUseStorage(input.storage)) {
      try {
        input.storage.setItem(
          VISUAL_TRACKING_PREVIEW_PLATFORM_SESSION_KEY,
          platform
        );
      } catch (error) {
        logger.warn('save visual tracking preview platform failed', error);
      }
    }

    return platform;
  }

  if (!canUseStorage(input.storage)) {
    return undefined;
  }

  try {
    const storedPlatform = input.storage.getItem(
      VISUAL_TRACKING_PREVIEW_PLATFORM_SESSION_KEY
    );
    return isPreviewPlatform(storedPlatform) ? storedPlatform : undefined;
  } catch (error) {
    logger.warn('read visual tracking preview platform failed', error);
    return undefined;
  }
}

/** 预览平台覆盖优先，否则走设备检测（入口与路由共用） */
export function resolveAppIsMobile(): boolean {
  const storage = typeof window === 'undefined' ? null : window.sessionStorage;
  const search = typeof window === 'undefined' ? '' : window.location.search;
  const previewPlatform = resolveVisualTrackingDeviceOverride(
    search,
    isCurrentWindowFramed(),
    storage
  );

  if (previewPlatform) {
    return previewPlatform === 'mobile';
  }

  return Device.isMobile();
}

export function resolveVisualTrackingDeviceOverride(
  search: string,
  isFramed: boolean,
  storage?: Storage | null
): VisualTrackingPreviewPlatform | undefined {
  const isPreviewMode = resolveVisualTrackingPreviewMode({
    search,
    isFramed,
    storage,
  });

  return resolveVisualTrackingPreviewPlatform({
    search,
    isPreviewMode,
    isFramed,
    storage,
  });
}

export function isCurrentWindowFramed(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    return window.self !== window.top;
  } catch (error) {
    return true;
  }
}

export function shouldHideRouteInVisualTrackingPreview(
  routePath: string,
  isPreviewMode: boolean
): boolean {
  if (!isPreviewMode || typeof routePath !== 'string') {
    return false;
  }

  const normalizedPath = routePath
    .replace(/^\/org\/[^/]+\//, '')
    .replace(/^\/+/, '')
    .replace(/\/+$/, '');

  return normalizedPath === VISUAL_TRACKING_WORKBENCH_ROUTE;
}
